import classNames from 'classnames';
import { Field, Formik } from 'formik';
import { useIntl } from 'react-intl';
import { LOADING_TYPES, useCheckoutState } from '../../../utils/checkoutState';
import { config, getAssetUrl } from '../../../utils/config';
import getValidator from '../../../utils/getValidator';
import { pushErrorTrackingEventFromErrorObject, pushErrorTrackingEventFromI18nKey } from '../../../utils/tracking';
import InputWithIconAndButtonField from '../../formik/InputWithIconAndButtonField';
import { showDefaultError, showNotification } from '../Notifications';
import PromotionList from './PromotionList';
import VoucherList from './VoucherList';
import './style.scss';

const DiscountCodes = () => {
  const { checkoutState, addVoucher, removeVoucher, addPromotion, removePromotion, isLoading, setLoading } =
    useCheckoutState();
  const { formatMessage } = useIntl();
  const { promotion } = checkoutState.integrations;
  const { vouchers = [], promotions: appliedPromotions = [] } = checkoutState.orderState;

  const areGiftCardsEnabled = config.features.giftCards.enabled;
  const arePromotionsEnabled = promotion?.codes?.enabled;
  const { maxLength, minLength } = config.features.vouchers;

  const initialValues = {
    code: ''
  };

  const addHandler = async (
    { code }: typeof initialValues,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void
  ) => {
    if (code === '') {
      return;
    }
    setLoading(LOADING_TYPES.voucherAdd);
    try {
      await (arePromotionsEnabled ? addPromotion({ code }) : addVoucher({ code }));
      if (arePromotionsEnabled) {
        setFieldValue('code', '');
      }
    } catch (e: any) {
      if ([404, 422].includes(e.statusCode)) {
        // TODO: https://aboutyou.atlassian.net/browse/CT-278894 - check if the status codes for promotions will be different
        const errorMessage = `payment.voucher.notifications.${e.statusCode === 404 ? 'notFound' : 'tooShort'}`;
        showNotification({
          level: 'error',
          text: formatMessage({ id: errorMessage }),
          autoClose: true,
          closable: true
        });
      } else {
        showDefaultError();
      }
      console.error(e);
      pushErrorTrackingEventFromErrorObject(e, arePromotionsEnabled ? 'submit' : 'add_voucher', code);
    }
    setLoading(LOADING_TYPES.off);
  };

  const removeHandler = async (identifier: string) => {
    setLoading(arePromotionsEnabled ? LOADING_TYPES.promotionRemove(identifier) : LOADING_TYPES.voucherRemove(identifier));
    try {
        await(arePromotionsEnabled ? removePromotion({ identifier }): removeVoucher({ identifier }));
    } catch (e) {
      console.error(e);
      showDefaultError();
      pushErrorTrackingEventFromErrorObject(e, arePromotionsEnabled ? 'remove' : 'remove_voucher');
    }
    setLoading(LOADING_TYPES.off);
  };

  return (
    <>
      {!!vouchers.length && !arePromotionsEnabled && (
        <VoucherList
          vouchers={vouchers}
          isLoading={isLoading}
          areGiftCardsEnabled={areGiftCardsEnabled}
          removeHandler={removeHandler}
        />
      )}
      {(arePromotionsEnabled || !vouchers.length) && (
        <Formik
          onSubmit={(values, { setFieldValue }) => addHandler(values, setFieldValue)}
          initialValues={initialValues}
        >
          {({ submitForm, values }) => (
            <Field
              component={InputWithIconAndButtonField}
              icon={arePromotionsEnabled && { src: getAssetUrl('discount/discount.svg') }}
              button={{
                text: 'generic.add',
                onClick: () => {
                  if (minLength && values.code.length < minLength) {
                    pushErrorTrackingEventFromI18nKey(
                      'payment.voucher.notifications.tooShort',
                      arePromotionsEnabled ? 'submit' : 'add_voucher',
                      values.code
                    );
                  }
                  submitForm();
                },
                disabled: !!isLoading,
                dataTestId: 'voucher-add-btn'
              }}
              placeholder={formatMessage({ id: 'payment.voucher.label' })}
              className={classNames('voucher__input', {
                'voucher__input--with-vouchers': !!vouchers.length && !arePromotionsEnabled,
                'voucher__input--with-giftcard': areGiftCardsEnabled
              })}
              name="code"
              isLoading={isLoading === LOADING_TYPES.voucherAdd}
              maxLength={maxLength}
              validate={getValidator(config.features.vouchers)}
              isInputStatusIconDisabled
              data-test-id="voucher-code"
            />
          )}
        </Formik>
      )}
      {!!appliedPromotions.length && arePromotionsEnabled && (
        <PromotionList promotions={appliedPromotions} isLoading={isLoading} removeHandler={removeHandler} />
      )}
      <slot name="slot_promotions" />
    </>
  );
};

export default DiscountCodes;
